// T014: Game Store
// 整合 EventLogger 用於遊戲事件記錄

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type {
  GameState,
  TurnPhase,
  BattlePosition,
  BattlefieldPosition,
  CardDefinition,
  AttackDeclaration,
} from '../types';
import {
  createInitialGameState,
  getActivePlayer,
  setPhase,
  switchActivePlayer,
  updatePlayer,
  updateBattlefield,
  addAttackDeclaration,
  clearAttackDeclarations,
  startCountdown,
  decrementCountdown,
  isCountdownFinished,
  endGame,
  determineWinner,
  addAction,
} from '../core/models/GameState';
import { drawCards, isDeckEmpty } from '../core/models/Player';
import { placeUnit, isSlotEmpty } from '../core/models/Battlefield';
import { setCardOrientation } from '../core/models/Card';
import { TurnManager } from '../core/state/TurnManager';
import { CombatSystem } from '../core/systems/CombatSystem';
import { DefensePhaseHandler } from '../core/rules/DefensePhaseHandler';
import { EndPhaseHandler } from '../core/rules/EndPhaseHandler';
import { EventLogger } from '../core/events/EventLogger';
import { effectTriggerSystem } from '../core/effects/EffectTriggerSystem';
import {
  ChoiceAwareExecutor,
  createChoiceAwareExecutor,
  createChoiceValidator,
  getChoiceManager,
  type PendingChoice,
  type ChoiceResult,
} from '../core/effects';
import type { SerializedEventLog } from '../types/events';
import { mapServerViewToGameState, extractPendingChoice } from '../services/OnlineStateMapper';
import { getWSClient } from '../services/NetworkManager';
import { loadAllCards } from '../services/CardLoader';
import { getGlobalTimer, resetGlobalTimer } from '../core/systems/TimerManager';
import { AudioManager } from '../services/AudioManager';
import { getLogger } from '../utils/logger';

const log = getLogger('gameStore');

interface GameStore {
  // State
  gameState: GameState | null;
  cardDefinitions: Map<string, CardDefinition>;
  isInitialized: boolean;
  eventLogger: EventLogger | null;

  // Actions
  initGame: (
    player1Config: { id: string; name: string; faction: string; deck: CardDefinition[] },
    player2Config: { id: string; name: string; faction: string; deck: CardDefinition[] }
  ) => void;

  setCardDefinitions: (definitions: CardDefinition[]) => void;

  // Phase actions (legacy)
  executeDrawPhase: () => void;
  executeSummonPhase: (cardId: string, position: BattlePosition) => boolean;
  declareAttack: (cardIds: string[]) => void;
  executeTacticalDeploy: (cardId: string, position: BattlePosition) => boolean;
  endPhase: () => void;

  // TurnManager-based actions (new)
  turnExecuteDraw: () => { success: boolean; error?: string };
  turnExecuteSummon: (cardId: string, position: BattlefieldPosition) => { success: boolean; error?: string };
  turnExecuteAttack: (attackerIds: string[]) => { success: boolean; error?: string };
  turnExecuteMove: (from: BattlefieldPosition, to: BattlefieldPosition) => { success: boolean; error?: string };
  turnExecuteSwap: (from: BattlefieldPosition, to: BattlefieldPosition) => { success: boolean; error?: string };
  turnExecuteTacticalSummon: (cardId: string, position: BattlefieldPosition) => { success: boolean; error?: string };
  turnEndPhase: () => { success: boolean; error?: string };
  getAvailableActions: () => string[];
  getCurrentPhaseNameZh: () => string;

  // Combat actions
  resolveCombat: () => {
    success: boolean;
    destroyedUnits: string[];
    upgradedUnits: { from: string; to: string; unitId: string }[];
    combatResults: import('../types').CombatResult[];
    messages: string[];
  };
  executeDefensePhase: () => {
    success: boolean;
    combatOccurred: boolean;
    destroyedUnits: string[];
    upgradedUnits: { from: string; to: string; unitId: string }[];
    combatResults: import('../types').CombatResult[];
    messages: string[];
  };
  executeEndPhase: () => { success: boolean; gameEnded: boolean };
  getScores: () => { player1: number; player2: number };

  // Game flow
  checkGameEnd: () => boolean;
  resetGame: () => void;

  // Event Logger
  getEventLog: () => SerializedEventLog | null;
  getRecentEvents: (count: number) => import('../types/events').GameEvent[];

  // 選擇系統
  pendingChoice: PendingChoice | null;
  pendingExecutionId: string | null;
  getPendingChoice: () => PendingChoice | null;
  hasPendingChoice: () => boolean;
  submitChoice: (result: ChoiceResult) => { success: boolean; error?: string };
  cancelChoice: () => void;
  setPendingChoiceFromServer: (choice: PendingChoice) => void;
  getValidTargets: () => import('../types').Card[];

  // 魔法/法術系統
  castSpell: (cardId: string, targets?: string[]) => { success: boolean; error?: string };
  placeFieldMagic: (cardId: string) => { success: boolean; error?: string };
  removeFieldMagic: (playerId: string) => { success: boolean; error?: string };
  getFieldMagic: (playerId: string) => import('../types').Card | null;

  // ============================================================
  // 連線對戰模式
  // ============================================================
  gameMode: 'local' | 'online';
  onlineGameId: string | null;
  onlinePlayerSide: 'player1' | 'player2' | null;

  /** 開始連線對戰（收到 game_start 事件時呼叫） */
  startOnlineGame: (gameId: string, playerSide: 'player1' | 'player2', initialState: unknown) => void;
  /** 處理伺服器推送的遊戲事件 */
  handleOnlineEvent: (event: { event: string; data?: Record<string, unknown>; [key: string]: unknown }) => void;
  /** 處理遊戲結束 */
  handleOnlineGameEnd: (winnerId: string, winCondition: string, isWinner: boolean) => void;
  /** 更新完整遊戲狀態（重連後） */
  updateOnlineGameState: (state: unknown) => void;
}

// 輔助函數：取得卡牌名稱
function getCardName(
  cardId: string,
  definitionId: string,
  definitions: Map<string, CardDefinition>
): string {
  const def = definitions.get(definitionId);
  return def?.nameZh || def?.name || cardId;
}

// 輔助函數：取得完整卡牌資訊（用於事件記錄）
interface CardDetails {
  cardId: string;
  cardName: string;
  cardDefinitionId: string;
  cardType: string;
  faction: string;
  atk: number | null;
  score: number | null;
  effectText: string | null;
  keywords: string[];
  rarity: string | null;
  sacrificeRequired: number | null;
}

function getCardDetails(
  cardId: string,
  definitionId: string,
  definitions: Map<string, CardDefinition>,
  runtimeCard?: { keywords?: string[]; atkModifier?: number; scoreModifier?: number }
): CardDetails {
  const def = definitions.get(definitionId);
  const baseAtk = def?.atk ?? null;
  const baseScore = def?.score ?? null;
  
  return {
    cardId,
    cardName: def?.nameZh || def?.name || cardId,
    cardDefinitionId: definitionId,
    cardType: def?.type || 'unknown',
    faction: def?.faction || 'unknown',
    atk: baseAtk !== null ? baseAtk + (runtimeCard?.atkModifier ?? 0) : null,
    score: baseScore !== null ? baseScore + (runtimeCard?.scoreModifier ?? 0) : null,
    effectText: def?.effectText || null,
    keywords: runtimeCard?.keywords || [],
    rarity: def?.rarity || null,
    sacrificeRequired: def?.sacrificeRequired ?? null,
  };
}

// 輔助函數：取得玩家顯示名稱
function getPlayerDisplayName(playerId: string, gameState: GameState | null): string {
  if (!gameState) return playerId;
  if (playerId === 'player1') return gameState.player1.name || '玩家';
  if (playerId === 'player2') return gameState.player2.name || '對手';
  return playerId;
}

/**
 * 記錄效果觸發事件（增強版）
 */
function logEffectEvents(
  eventLogger: EventLogger | null,
  card: { id: string; definitionId: string; keywords?: string[] },
  cardName: string,
  effectText: string | undefined,
  triggerType: string,
  messages: string[],
  playerId: string,
  gameState: GameState | null,
  cardDefinitions?: Map<string, CardDefinition>
): void {
  if (!eventLogger || messages.length === 0) return;

  // 取得更多卡牌資訊
  const cardDef = cardDefinitions?.get(card.definitionId);
  const playerName = getPlayerDisplayName(playerId, gameState);
  
  // 記錄效果觸發
  const triggerId = eventLogger.log('EFFECT_TRIGGER', {
    // 效果資訊
    effectName: effectText?.split('\n')[0]?.substring(0, 80) || '效果',
    fullEffectText: effectText || null,
    triggerType,
    // 來源卡牌詳細資訊
    sourceCardId: card.id,
    sourceCardName: cardName,
    sourceCardDefinitionId: card.definitionId,
    sourceCardType: cardDef?.type || 'unknown',
    sourceCardAtk: cardDef?.atk ?? null,
    sourceCardScore: cardDef?.score ?? null,
    sourceCardKeywords: card.keywords || [],
    // 玩家資訊
    playerId,
    playerName,
    // 遊戲狀態資訊
    turnNumber: gameState?.turnNumber ?? 0,
    currentPhase: gameState?.currentPhase ?? 'unknown',
  }, { playerId }).id;

  // 記錄效果結果
  eventLogger.log('EFFECT_RESOLVE', {
    effectName: effectText?.split('\n')[0]?.substring(0, 80) || '效果',
    sourceCardId: card.id,
    sourceCardName: cardName,
    result: 'success',
    changes: messages,
    // 增強資訊
    playerId,
    playerName,
    turnNumber: gameState?.turnNumber ?? 0,
    currentPhase: gameState?.currentPhase ?? 'unknown',
  }, { playerId, triggeredBy: triggerId });
}

// 選擇感知執行器實例
let choiceExecutor: ChoiceAwareExecutor | null = null;

export const useGameStore = create<GameStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      gameState: null,
      cardDefinitions: new Map(),
      isInitialized: false,
      eventLogger: null,
      pendingChoice: null,
      pendingExecutionId: null,

      initGame: (player1Config, player2Config) => {
        const state = createInitialGameState(
          { id: player1Config.id, name: player1Config.name, faction: player1Config.faction },
          { id: player2Config.id, name: player2Config.name, faction: player2Config.faction }
        );

        // Build card definitions map from existing + new definitions
        const { cardDefinitions: existingDefs } = get();
        const definitions = new Map<string, CardDefinition>(existingDefs);
        [...player1Config.deck, ...player2Config.deck].forEach((def) => {
          definitions.set(def.id, def);
        });

        // 建立 EventLogger
        const logger = new EventLogger();
        logger.updateContext({
          turnNumber: 1,
          phase: 'summon',
          activePlayerId: player1Config.id,
        });

        // 記錄遊戲開始事件
        logger.log('GAME_START', {
          turnNumber: 1,
          activePlayerId: player1Config.id,
          activePlayerName: player1Config.name,
        });

        // Create card instances for player 1's deck
        const player1Deck = player1Config.deck.map((def, index) => ({
          id: `p1_${def.id}_${String(index)}`,
          definitionId: def.id,
          ownerId: player1Config.id,
          location: 'deck' as const,
          orientation: 'upright' as const,
        }));

        // Create card instances for player 2's deck
        const player2Deck = player2Config.deck.map((def, index) => ({
          id: `p2_${def.id}_${String(index)}`,
          definitionId: def.id,
          ownerId: player2Config.id,
          location: 'deck' as const,
          orientation: 'upright' as const,
        }));

        // Shuffle decks
        const shuffledDeck1 = [...player1Deck].sort(() => Math.random() - 0.5);
        const shuffledDeck2 = [...player2Deck].sort(() => Math.random() - 0.5);

        // Draw initial hands (5 cards each)
        const player1Hand = shuffledDeck1.splice(0, 5).map(c => ({ ...c, location: 'hand' as const }));
        const player2Hand = shuffledDeck2.splice(0, 5).map(c => ({ ...c, location: 'hand' as const }));

        // 記錄初始抽牌
        player1Hand.forEach(card => {
          logger.log('CARD_DRAW', {
            cardId: card.id,
            cardName: getCardName(card.id, card.definitionId, definitions),
            from: { zone: 'deck' },
            to: { zone: 'hand' },
          }, { playerId: player1Config.id });
        });
        player2Hand.forEach(card => {
          logger.log('CARD_DRAW', {
            cardId: card.id,
            cardName: getCardName(card.id, card.definitionId, definitions),
            from: { zone: 'deck' },
            to: { zone: 'hand' },
          }, { playerId: player2Config.id, visibility: 'player2' });
        });

        // Update player states
        const updatedState: GameState = {
          ...state,
          player1: {
            ...state.player1,
            deck: shuffledDeck1,
            hand: player1Hand,
          },
          player2: {
            ...state.player2,
            deck: shuffledDeck2,
            hand: player2Hand,
          },
        };

        // Store cardDefinitions in TurnManager for combat resolution
        TurnManager.setCardDefinitions(definitions);

        // First turn starts at summon phase (skip draw & defense since starting hand already drawn)
        set({
          gameState: updatedState,
          cardDefinitions: definitions,
          isInitialized: true,
          eventLogger: logger,
        });
      },

      setCardDefinitions: (definitions) => {
        const map = new Map<string, CardDefinition>();
        definitions.forEach((def) => map.set(def.id, def));
        set({ cardDefinitions: map });
      },

      executeDrawPhase: () => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState || gameState.currentPhase !== 'draw') return;

        log.debug(`[gameStore.executeDrawPhase] Called for ${gameState.activePlayerId}`);
        log.debug(`[gameStore.executeDrawPhase] Attack declarations: ${String(gameState.attackDeclarations.length)}`);

        const activePlayer = getActivePlayer(gameState);

        // Check if deck is empty - start countdown
        if (isDeckEmpty(activePlayer)) {
          const newState = startCountdown(gameState);
          set({ gameState: setPhase(newState, 'defense') });
          return;
        }

        // Draw 2 cards
        const updatedPlayer = drawCards(activePlayer, 2);
        let newState = updatePlayer(gameState, updatedPlayer);
        newState = addAction(newState, {
          type: 'DRAW_CARDS',
          playerId: activePlayer.id,
          count: 2,
        });

        // 記錄抽牌事件
        if (eventLogger) {
          const drawnCards = updatedPlayer.hand.slice(-2);
          drawnCards.forEach(card => {
            eventLogger.log('CARD_DRAW', {
              cardId: card.id,
              cardName: getCardName(card.id, card.definitionId, cardDefinitions),
              from: { zone: 'deck' },
              to: { zone: 'hand' },
            }, { playerId: activePlayer.id });
          });
        }

        // Check if deck became empty after drawing
        if (isDeckEmpty(updatedPlayer)) {
          newState = startCountdown(newState);
        }

        // If there are pending attacks, execute defense phase to resolve combat
        const hasAttacks = newState.attackDeclarations.length > 0;
        if (hasAttacks) {
          log.debug('[gameStore.executeDrawPhase] Has attacks, executing defense phase...');
          // Import and execute defense phase
          const defenseResult = DefensePhaseHandler.execute(newState, cardDefinitions);
          newState = defenseResult.state;

          // 記錄戰鬥結果
          if (eventLogger && defenseResult.combatOccurred) {
            defenseResult.combatResults.forEach(result => {
              const attackerWins = result.winner === 'attacker';
              eventLogger.log('COMBAT_RESULT', {
                column: 1, // CombatResult 沒有 column，使用預設值
                result: attackerWins ? 'attacker_wins' : 'defender_wins',
                attackerSurvived: attackerWins,
                defenderSurvived: !attackerWins,
              });
            });
            defenseResult.destroyedUnits.forEach(unitId => {
              eventLogger.log('UNIT_DESTROYED', {
                cardId: unitId,
                cardName: unitId,
                position: { side: 'player1' as const, row: 'front' as const, column: 1 as const },
                destroyedBy: 'combat',
              });
            });
          }

          log.debug('[gameStore.executeDrawPhase] Combat occurred:', defenseResult.combatOccurred, 'destroyed:', defenseResult.destroyedUnits);

          // Clear attack declarations after combat is resolved
          newState = clearAttackDeclarations(newState);
        }

        // Move to summon phase (defense was handled above if needed)
        newState = setPhase(newState, 'summon');

        // 記錄階段變更
        if (eventLogger) {
          eventLogger.updateContext({ phase: 'summon' });
          eventLogger.log('PHASE_CHANGE', {
            previousPhase: 'draw' as TurnPhase,
            newPhase: 'summon' as TurnPhase,
            turnNumber: gameState.turnNumber,
          });
        }

        set({ gameState: newState });
      },

      executeSummonPhase: (cardId, position) => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState || gameState.currentPhase !== 'summon') return false;

        const activePlayer = getActivePlayer(gameState);
        const card = activePlayer.hand.find((c) => c.id === cardId);

        if (!card) return false;

        // Convert BattlePosition to BattlefieldPosition (needed for isSlotEmpty)
        const bfPosition: BattlefieldPosition = {
          side: activePlayer.id === gameState.player1.id ? 'player1' : 'player2',
          row: position.row,
          column: position.column,
        };

        if (!isSlotEmpty(gameState.battlefield, bfPosition)) return false;

        // Create card with position info for battlefield
        const summonedCard = {
          ...card,
          location: 'battlefield' as const,
          position: bfPosition,
          summonedThisTurn: true,
        };

        // Place unit on battlefield
        const newBattlefield = placeUnit(gameState.battlefield, position, summonedCard);

        // Remove card from hand
        const updatedPlayer = {
          ...activePlayer,
          hand: activePlayer.hand.filter((c) => c.id !== cardId),
        };

        let newState = updatePlayer(gameState, updatedPlayer);
        newState = updateBattlefield(newState, newBattlefield);
        newState = addAction(newState, {
          type: 'SUMMON_UNIT',
          playerId: activePlayer.id,
          cardId,
          position,
        });

        // 🎯 觸發召喚時效果（使用選擇感知系統）
        effectTriggerSystem.setDefinitions(cardDefinitions);
        const triggerResult = effectTriggerSystem.triggerOnSummon(newState, summonedCard, bfPosition);

        // 初始化選擇感知執行器
        if (!choiceExecutor) {
          choiceExecutor = createChoiceAwareExecutor(cardDefinitions);
        }

        // 執行效果
        if (triggerResult.immediateActions.length > 0) {
          const execResult = choiceExecutor.execute(newState, triggerResult.immediateActions, {
            sourceCardId: summonedCard.id,
            sourcePosition: bfPosition,
          });

          newState = execResult.state;

          // 檢查是否需要玩家選擇
          if (execResult.status === 'pending_choice' && execResult.pendingChoice) {
            set({
              gameState: newState,
              pendingChoice: execResult.pendingChoice,
              pendingExecutionId: execResult.executionId ?? null,
            });
            log.debug('Effect requires choice:', execResult.pendingChoice.effectText);
            return true; // 召喚成功，但等待選擇
          }

          if (execResult.messages.length > 0) {
            log.debug('Summon effects:', execResult.messages.join(', '));
          }
        }

        // 記錄召喚事件（增強版，包含完整卡牌資訊）
        if (eventLogger) {
          const cardDetails = getCardDetails(card.id, card.definitionId, cardDefinitions, card);
          const playerName = getPlayerDisplayName(activePlayer.id, gameState);
          eventLogger.log('CARD_SUMMON', {
            // 基本資訊
            cardId: card.id,
            cardName: cardDetails.cardName,
            cardDefinitionId: card.definitionId,
            position,
            fromZone: 'hand',
            isTactical: false,
            // 增強資訊：玩家
            playerName,
            playerId: activePlayer.id,
            // 增強資訊：卡牌數值
            cardType: cardDetails.cardType,
            faction: cardDetails.faction,
            atk: cardDetails.atk,
            score: cardDetails.score,
            // 增強資訊：效果與關鍵字
            effectText: cardDetails.effectText,
            keywords: cardDetails.keywords,
            sacrificeRequired: cardDetails.sacrificeRequired,
            rarity: cardDetails.rarity,
          }, { playerId: activePlayer.id });
        }

        set({ gameState: newState });
        return true;
      },

      declareAttack: (cardIds) => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState || gameState.currentPhase !== 'tactics') return;

        let newState = gameState;

        for (const cardId of cardIds) {
          // Find the card on battlefield
          for (const slot of [
            ...gameState.battlefield.player1Side.frontLine,
            ...gameState.battlefield.player1Side.backLine,
            ...gameState.battlefield.player2Side.frontLine,
            ...gameState.battlefield.player2Side.backLine,
          ]) {
            if (slot.unit?.id === cardId) {
              // Tilt the card
              const tiltedCard = setCardOrientation(slot.unit, 'tilted');
              const newBattlefield = placeUnit(
                newState.battlefield,
                slot.position,
                tiltedCard
              );
              newState = updateBattlefield(newState, newBattlefield);

              // Add attack declaration
              const declaration: AttackDeclaration = {
                attackerId: cardId,
                attackerPosition: slot.position,
                targetColumn: slot.position.column,
              };
              newState = addAttackDeclaration(newState, declaration);

              // 記錄攻擊宣告（增強版）
              if (eventLogger) {
                const cardDetails = getCardDetails(cardId, slot.unit.definitionId, cardDefinitions, slot.unit);
                const playerName = getPlayerDisplayName(gameState.activePlayerId, gameState);
                eventLogger.log('ATTACK_DECLARE', {
                  // 攻擊者資訊
                  cardId,
                  cardName: cardDetails.cardName,
                  cardDefinitionId: slot.unit.definitionId,
                  atk: cardDetails.atk,
                  score: cardDetails.score,
                  keywords: cardDetails.keywords,
                  // 位置資訊
                  attackerPosition: slot.position,
                  targetColumn: slot.position.column,
                  // 玩家資訊
                  playerName,
                  playerId: gameState.activePlayerId,
                }, { playerId: gameState.activePlayerId });
              }
            }
          }
        }

        newState = addAction(newState, {
          type: 'DECLARE_ATTACK',
          cardIds,
        });

        set({ gameState: newState });
      },

      executeTacticalDeploy: (cardId, position) => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState || gameState.currentPhase !== 'tactics') return false;

        // Similar to summon but limited to 1 card
        const activePlayer = getActivePlayer(gameState);
        const card = activePlayer.hand.find((c) => c.id === cardId);

        if (!card) return false;
        if (!isSlotEmpty(gameState.battlefield, position)) return false;

        // Convert BattlePosition to BattlefieldPosition
        const bfPosition: BattlefieldPosition = {
          side: activePlayer.id === gameState.player1.id ? 'player1' : 'player2',
          row: position.row,
          column: position.column,
        };

        const summonedCard = {
          ...card,
          location: 'battlefield' as const,
          position: bfPosition,
          summonedThisTurn: true,
        };

        const newBattlefield = placeUnit(gameState.battlefield, position, summonedCard);
        const updatedPlayer = {
          ...activePlayer,
          hand: activePlayer.hand.filter((c) => c.id !== cardId),
        };

        let newState = updatePlayer(gameState, updatedPlayer);
        newState = updateBattlefield(newState, newBattlefield);
        newState = addAction(newState, {
          type: 'TACTICAL_DEPLOY',
          cardId,
          position,
        });

        // 🎯 觸發並執行召喚時效果
        effectTriggerSystem.setDefinitions(cardDefinitions);
        const effectResult = effectTriggerSystem.triggerAndExecuteOnSummon(
          newState,
          summonedCard,
          bfPosition
        );
        newState = effectResult.state;

        // 取得卡牌定義以獲取效果文字
        const cardDef = cardDefinitions.get(card.definitionId);
        const cardDisplayName = getCardName(card.id, card.definitionId, cardDefinitions);

        // 記錄效果事件（增強版）
        logEffectEvents(
          eventLogger,
          card,
          cardDisplayName,
          cardDef?.effectText,
          'onSummon',
          effectResult.messages,
          activePlayer.id,
          gameState,
          cardDefinitions
        );

        if (effectResult.messages.length > 0) {
          log.debug('Tactical deploy effects:', effectResult.messages.join(', '));
        }

        // 記錄戰術部署（召喚）事件（增強版）
        if (eventLogger) {
          const cardDetails = getCardDetails(card.id, card.definitionId, cardDefinitions, card);
          const playerName = getPlayerDisplayName(activePlayer.id, gameState);
          eventLogger.log('CARD_SUMMON', {
            // 基本資訊
            cardId: card.id,
            cardName: cardDisplayName,
            cardDefinitionId: card.definitionId,
            position,
            fromZone: 'hand',
            isTactical: true,
            // 增強資訊：玩家
            playerName,
            playerId: activePlayer.id,
            // 增強資訊：卡牌數值
            cardType: cardDetails.cardType,
            faction: cardDetails.faction,
            atk: cardDetails.atk,
            score: cardDetails.score,
            // 增強資訊：效果與關鍵字
            effectText: cardDetails.effectText,
            keywords: cardDetails.keywords,
            sacrificeRequired: cardDetails.sacrificeRequired,
            rarity: cardDetails.rarity,
            // 回合資訊
            turnNumber: gameState.turnNumber,
            currentPhase: gameState.currentPhase,
          }, { playerId: activePlayer.id, tags: ['tactical'] });
        }

        set({ gameState: newState });
        return true;
      },

      endPhase: () => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState) return;

        const phases: TurnPhase[] = ['draw', 'defense', 'summon', 'tactics', 'end'];
        const currentIndex = phases.indexOf(gameState.currentPhase);
        const previousPhase = gameState.currentPhase;

        if (gameState.currentPhase === 'end') {
          // End of turn - switch players
          let newState = switchActivePlayer(gameState);
          const newTurnNumber = newState.turnNumber;

          // 記錄回合結束（增強版）
          if (eventLogger) {
            const endingPlayer = gameState.activePlayerId === 'player1' ? gameState.player1 : gameState.player2;
            eventLogger.log('TURN_END', {
              turnNumber: gameState.turnNumber,
              // 結束回合的玩家資訊
              endingPlayerId: gameState.activePlayerId,
              endingPlayerName: endingPlayer.name,
              // 回合結束時的遊戲狀態摘要
              player1DeckCount: gameState.player1.deck.length,
              player1HandCount: gameState.player1.hand.length,
              player1GraveyardCount: gameState.player1.graveyard.length,
              player2DeckCount: gameState.player2.deck.length,
              player2HandCount: gameState.player2.hand.length,
              player2GraveyardCount: gameState.player2.graveyard.length,
            });

            // 記錄新回合開始
            eventLogger.updateContext({
              turnNumber: newTurnNumber,
              phase: 'draw',
              activePlayerId: newState.activePlayerId,
            });
            const startingPlayer = newState.activePlayerId === 'player1' ? newState.player1 : newState.player2;
            eventLogger.log('TURN_START', {
              turnNumber: newTurnNumber,
              activePlayerId: newState.activePlayerId,
              activePlayerName: startingPlayer.name,
              // 新回合開始時的玩家資源
              playerDeckCount: startingPlayer.deck.length,
              playerHandCount: startingPlayer.hand.length,
              playerGraveyardCount: startingPlayer.graveyard.length,
            });
          }

          // Decrement countdown if active
          if (newState.countdownTurns !== null) {
            newState = decrementCountdown(newState);

            // Check if countdown finished
            if (isCountdownFinished(newState)) {
              const winnerId = determineWinner(newState, cardDefinitions);
              newState = endGame(newState, winnerId);

              // 記錄遊戲結束
              if (eventLogger) {
                const winner = winnerId === 'player1' ? newState.player1 : newState.player2;
                const loser = winnerId === 'player1' ? newState.player2 : newState.player1;
                eventLogger.log('GAME_END', {
                  winnerId,
                  winnerName: winner.name,
                  loserId: loser.id,
                  loserName: loser.name,
                  winCondition: 'countdown',
                  finalScores: (() => {
                    const s = EndPhaseHandler.getScores(newState, cardDefinitions);
                    return { player1Score: s.player1 + (newState.player1.reward ?? 0), player2Score: s.player2 + (newState.player2.reward ?? 0) };
                  })(),
                  totalTurns: newState.turnNumber,
                });
              }
            }
          }

          // Attack declarations are cleared in executeDrawPhase after combat resolves

          set({ gameState: newState });
        } else {
          // Move to next phase
          const nextPhase = phases[currentIndex + 1];

          // 記錄階段變更
          if (eventLogger) {
            eventLogger.updateContext({ phase: nextPhase });
            eventLogger.log('PHASE_CHANGE', {
              previousPhase,
              newPhase: nextPhase,
              turnNumber: gameState.turnNumber,
            });
          }

          set({ gameState: setPhase(gameState, nextPhase) });
        }
      },

      checkGameEnd: () => {
        const { gameState } = get();
        return gameState?.gameEnded ?? false;
      },

      resetGame: () => {
        resetGlobalTimer();
        set({
          gameState: null,
          isInitialized: false,
          eventLogger: null,
          gameMode: 'local' as const,
          onlineGameId: null,
          onlinePlayerSide: null,
        });
      },

      // TurnManager-based actions
      turnExecuteDraw: () => {
        const { gameState, eventLogger, cardDefinitions } = get();
        if (!gameState) return { success: false, error: 'Game not initialized' };

        const result = TurnManager.executeDraw(gameState);
        if (result.success && eventLogger) {
          const activePlayer = getActivePlayer(result.state);
          const drawnCards = activePlayer.hand.slice(-2);
          drawnCards.forEach(card => {
            const cardDetails = getCardDetails(card.id, card.definitionId, cardDefinitions, card);
            const playerName = getPlayerDisplayName(activePlayer.id, gameState);
            eventLogger.log('CARD_DRAW', {
              // 基本卡牌資訊
              cardId: card.id,
              cardName: cardDetails.cardName,
              cardDefinitionId: card.definitionId,
              // 增強卡牌資訊
              cardType: cardDetails.cardType,
              faction: cardDetails.faction,
              atk: cardDetails.atk,
              score: cardDetails.score,
              effectText: cardDetails.effectText,
              // 位置變更
              from: { zone: 'deck' as const },
              to: { zone: 'hand' as const },
              // 玩家資訊
              playerId: activePlayer.id,
              playerName,
              // 遊戲狀態
              turnNumber: gameState.turnNumber,
              currentPhase: gameState.currentPhase,
              remainingDeckCount: activePlayer.deck.length,
            }, { playerId: activePlayer.id });
          });
          set({ gameState: result.state });
        } else if (result.success) {
          set({ gameState: result.state });
        }
        return { success: result.success, error: result.success ? undefined : result.error };
      },

      turnExecuteSummon: (cardId, position) => {
        const { gameState, eventLogger, cardDefinitions } = get();
        if (!gameState) return { success: false, error: 'Game not initialized' };

        const activePlayer = getActivePlayer(gameState);
        const card = activePlayer.hand.find(c => c.id === cardId);

        const result = TurnManager.executeSummon(gameState, cardId, position);
        if (result.success && card) {
          let newState = result.state;

          // 🎯 觸發並執行召喚時效果
          effectTriggerSystem.setDefinitions(cardDefinitions);
          
          // 取得剛召喚的卡牌
          const summonedCard = {
            ...card,
            location: 'battlefield' as const,
            position,
            summonedThisTurn: true,
          };

          const effectResult = effectTriggerSystem.triggerAndExecuteOnSummon(
            newState,
            summonedCard,
            position
          );
          newState = effectResult.state;

          // 取得卡牌定義以獲取效果文字
          const cardDef = cardDefinitions.get(card.definitionId);
          const cardDisplayName = getCardName(card.id, card.definitionId, cardDefinitions);

          // 記錄效果事件（增強版）
          logEffectEvents(
            eventLogger,
            card,
            cardDisplayName,
            cardDef?.effectText,
            'onSummon',
            effectResult.messages,
            activePlayer.id,
            gameState,
            cardDefinitions
          );

          // 記錄效果訊息
          if (effectResult.messages.length > 0) {
            log.debug('Summon effects:', effectResult.messages.join(', '));
          }

          // 記錄召喚事件（增強版，包含完整卡牌資訊）
          if (eventLogger) {
            const cardDetails = getCardDetails(card.id, card.definitionId, cardDefinitions, card);
            const playerName = getPlayerDisplayName(activePlayer.id, gameState);
            eventLogger.log('CARD_SUMMON', {
              // 基本資訊
              cardId: card.id,
              cardName: cardDisplayName,
              cardDefinitionId: card.definitionId,
              position,
              fromZone: 'hand',
              isTactical: false,
              // 增強資訊：玩家
              playerName,
              playerId: activePlayer.id,
              // 增強資訊：卡牌數值
              cardType: cardDetails.cardType,
              faction: cardDetails.faction,
              atk: cardDetails.atk,
              score: cardDetails.score,
              // 增強資訊：效果與關鍵字
              effectText: cardDetails.effectText,
              keywords: cardDetails.keywords,
              sacrificeRequired: cardDetails.sacrificeRequired,
              rarity: cardDetails.rarity,
              // 回合資訊
              turnNumber: gameState.turnNumber,
              currentPhase: gameState.currentPhase,
            }, { playerId: activePlayer.id });
          }
          set({ gameState: newState });
        } else if (result.success) {
          set({ gameState: result.state });
        }
        return { success: result.success, error: result.success ? undefined : result.error };
      },

      turnExecuteAttack: (attackerIds) => {
        const { gameState, eventLogger } = get();
        if (!gameState) return { success: false, error: 'Game not initialized' };

        const result = TurnManager.executeAttack(gameState, attackerIds);
        if (result.success) {
          if (eventLogger) {
            eventLogger.log('ATTACK_DECLARE', {
              cardIds: attackerIds,
            }, { playerId: gameState.activePlayerId });
          }
          set({ gameState: result.state });
        }
        return { success: result.success, error: result.success ? undefined : result.error };
      },

      turnExecuteMove: (from, to) => {
        const { gameState, eventLogger, cardDefinitions } = get();
        if (!gameState) return { success: false, error: 'Game not initialized' };

        const result = TurnManager.executeMove(gameState, from, to);
        if (result.success) {
          if (eventLogger) {
            // 找到移動的卡牌
            const movedCard = result.state.battlefield[
              to.side === 'player1' ? 'player1Side' : 'player2Side'
            ][to.row === 'front' ? 'frontLine' : 'backLine'][to.column - 1].unit;
            
            if (movedCard) {
              eventLogger.log('CARD_MOVE', {
                cardId: movedCard.id,
                cardName: getCardName(movedCard.id, movedCard.definitionId, cardDefinitions),
                from: { zone: 'battlefield', position: from },
                to: { zone: 'battlefield', position: to },
              }, { playerId: gameState.activePlayerId });
            }
          }
          set({ gameState: result.state });
        }
        return { success: result.success, error: result.success ? undefined : result.error };
      },

      turnExecuteSwap: (from, to) => {
        const { gameState, eventLogger } = get();
        if (!gameState) return { success: false, error: 'Game not initialized' };

        const result = TurnManager.executeSwap(gameState, from, to);
        if (result.success) {
          if (eventLogger) {
            eventLogger.log('CARD_MOVE', {
              from: { zone: 'battlefield', position: from },
              to: { zone: 'battlefield', position: to },
              isSwap: true,
            }, { playerId: gameState.activePlayerId });
          }
          set({ gameState: result.state });
        }
        return { success: result.success, error: result.success ? undefined : result.error };
      },

      turnExecuteTacticalSummon: (cardId, position) => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState) return { success: false, error: 'Game not initialized' };

        const activePlayer = getActivePlayer(gameState);
        const card = activePlayer.hand.find(c => c.id === cardId);

        const result = TurnManager.executeTacticalSummon(gameState, cardId, position, cardDefinitions);
        if (result.success && card) {
          let newState = result.state;

          // 🎯 觸發並執行召喚時效果（戰術部署也是召喚）
          effectTriggerSystem.setDefinitions(cardDefinitions);
          
          const summonedCard = {
            ...card,
            location: 'battlefield' as const,
            position,
            summonedThisTurn: true,
          };

          const effectResult = effectTriggerSystem.triggerAndExecuteOnSummon(
            newState,
            summonedCard,
            position
          );
          newState = effectResult.state;

          // 取得卡牌定義以獲取效果文字
          const cardDef = cardDefinitions.get(card.definitionId);
          const cardDisplayName = getCardName(card.id, card.definitionId, cardDefinitions);

          // 記錄效果事件（增強版）
          logEffectEvents(
            eventLogger,
            card,
            cardDisplayName,
            cardDef?.effectText,
            'onSummon',
            effectResult.messages,
            activePlayer.id,
            gameState,
            cardDefinitions
          );

          if (effectResult.messages.length > 0) {
            log.debug('Tactical summon effects:', effectResult.messages.join(', '));
          }

          // 記錄戰術召喚事件（增強版，包含完整卡牌資訊）
          if (eventLogger) {
            const cardDetails = getCardDetails(card.id, card.definitionId, cardDefinitions, card);
            const playerName = getPlayerDisplayName(activePlayer.id, gameState);
            eventLogger.log('CARD_SUMMON', {
              // 基本資訊
              cardId: card.id,
              cardName: cardDisplayName,
              cardDefinitionId: card.definitionId,
              position,
              fromZone: 'hand',
              isTactical: true,
              // 增強資訊：玩家
              playerName,
              playerId: activePlayer.id,
              // 增強資訊：卡牌數值
              cardType: cardDetails.cardType,
              faction: cardDetails.faction,
              atk: cardDetails.atk,
              score: cardDetails.score,
              // 增強資訊：效果與關鍵字
              effectText: cardDetails.effectText,
              keywords: cardDetails.keywords,
              sacrificeRequired: cardDetails.sacrificeRequired,
              rarity: cardDetails.rarity,
              // 回合資訊
              turnNumber: gameState.turnNumber,
              currentPhase: gameState.currentPhase,
            }, { playerId: activePlayer.id, tags: ['tactical'] });
          }
          set({ gameState: newState });
        } else if (result.success) {
          set({ gameState: result.state });
        }
        return { success: result.success, error: result.success ? undefined : result.error };
      },

      turnEndPhase: () => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState) return { success: false, error: 'Game not initialized' };

        const previousPhase = gameState.currentPhase;
        const result = TurnManager.endPhase(gameState, cardDefinitions);
        
        if (result.success && eventLogger) {
          const newState = result.state;
          
          // 記錄階段變更
          if (newState.currentPhase !== previousPhase) {
            eventLogger.updateContext({ phase: newState.currentPhase });
            eventLogger.log('PHASE_CHANGE', {
              previousPhase,
              newPhase: newState.currentPhase,
              turnNumber: gameState.turnNumber,
            });
          }

          // 如果回合結束，記錄回合事件
          if (previousPhase === 'end' && newState.turnNumber !== gameState.turnNumber) {
            eventLogger.log('TURN_END', { turnNumber: gameState.turnNumber });
            eventLogger.updateContext({
              turnNumber: newState.turnNumber,
              activePlayerId: newState.activePlayerId,
            });
            eventLogger.log('TURN_START', {
              turnNumber: newState.turnNumber,
              activePlayerId: newState.activePlayerId,
              activePlayerName: newState.activePlayerId === 'player1' 
                ? newState.player1.name 
                : newState.player2.name,
            });
          }

          // 如果遊戲結束，記錄結束事件
          if (newState.gameEnded && !gameState.gameEnded && newState.winnerId) {
            const winnerId = newState.winnerId;
            const winner = winnerId === 'player1' ? newState.player1 : newState.player2;
            const loser = winnerId === 'player1' ? newState.player2 : newState.player1;
            eventLogger.log('GAME_END', {
              winnerId,
              winnerName: winner.name,
              loserId: loser.id,
              loserName: loser.name,
              winCondition: 'countdown',
              finalScores: (() => {
                const s = EndPhaseHandler.getScores(newState, cardDefinitions);
                return { player1Score: s.player1 + (newState.player1.reward ?? 0), player2Score: s.player2 + (newState.player2.reward ?? 0) };
              })(),
              totalTurns: newState.turnNumber,
            });
          }

          set({ gameState: newState });
        } else if (result.success) {
          set({ gameState: result.state });
        }
        return { success: result.success, error: result.success ? undefined : result.error };
      },

      getAvailableActions: () => {
        const { gameState } = get();
        if (!gameState) return [];
        return TurnManager.getAvailableActions(gameState);
      },

      getCurrentPhaseNameZh: () => {
        const { gameState } = get();
        if (!gameState) return '';
        return TurnManager.getPhaseNameZh(gameState);
      },

      // Combat actions
      resolveCombat: () => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState) {
          return {
            success: false,
            destroyedUnits: [],
            upgradedUnits: [],
            combatResults: [],
            messages: [],
          };
        }

        // 使用新的 CombatSystem
        const combatSystem = new CombatSystem({
          definitions: cardDefinitions,
          enableAutoUpgrade: true,
          enablePassengerEjection: true,
        });

        const result = combatSystem.resolveAllCombat(gameState);

        // 記錄戰鬥結果（增強版 - 包含誰攻擊誰、誰被摧毀）
        if (eventLogger) {
          result.combatResults.forEach(combat => {
            const attackerWins = combat.winner === 'attacker';
            
            // 解析卡牌所有者
            const attackerOwnerId = combat.attackerId.startsWith('p1_') ? 'player1' : 'player2';
            const defenderOwnerId: string | undefined = combat.defenderId 
              ? (combat.defenderId.startsWith('p1_') ? 'player1' : 'player2')
              : undefined;
            const attackerOwnerName = getPlayerDisplayName(attackerOwnerId, gameState);
            const defenderOwnerName = defenderOwnerId ? getPlayerDisplayName(defenderOwnerId, gameState) : '無';
            
            // 取得攻擊者和防禦者的完整資訊
            const attackerDefId = combat.attackerId.split('_').slice(1, 3).join('_');
            const defenderDefId = combat.defenderId.split('_').slice(1, 3).join('_');
            const attackerDef = cardDefinitions.get(attackerDefId);
            const defenderDef = cardDefinitions.get(defenderDefId);
            
            const attackerName = attackerDef?.nameZh || attackerDef?.name || combat.attackerId;
            const defenderName = defenderDef?.nameZh || defenderDef?.name || combat.defenderId || '無';
            
            // COMBAT_START: 記錄誰攻擊誰
            eventLogger.log('COMBAT_START', {
              column: combat.column ?? 1,
              turnNumber: gameState.turnNumber,
              // 攻擊者詳細資訊
              attackers: [{
                cardId: combat.attackerId,
                cardName: attackerName,
                cardDefinitionId: attackerDefId,
                atk: combat.attackerAtk,
                score: attackerDef?.score ?? null,
                position: combat.attackerPosition ?? { side: attackerOwnerId, row: 'front', column: (combat.column ?? 1) as 1 | 2 | 3 | 4 | 5 },
                ownerId: attackerOwnerId,
                ownerName: attackerOwnerName,
                keywords: [],
                effectText: attackerDef?.effectText ?? null,
              }],
              // 防禦者詳細資訊
              defenders: (combat.defenderId && defenderOwnerId) ? [{
                cardId: combat.defenderId,
                cardName: defenderName,
                cardDefinitionId: defenderDefId,
                atk: combat.defenderAtk,
                score: defenderDef?.score ?? null,
                position: combat.defenderPosition ?? { side: defenderOwnerId as 'player1' | 'player2', row: 'front', column: (combat.column ?? 1) as 1 | 2 | 3 | 4 | 5 },
                ownerId: defenderOwnerId,
                ownerName: defenderOwnerName,
                keywords: [],
                effectText: defenderDef?.effectText ?? null,
              }] : [],
            });

            // COMBAT_RESULT: 記錄戰鬥結果，誰贏誰輸
            eventLogger.log('COMBAT_RESULT', {
              column: combat.column ?? 1,
              turnNumber: gameState.turnNumber,
              result: attackerWins ? 'attacker_wins' : 'defender_wins',
              // 勝者資訊
              winnerId: attackerWins ? combat.attackerId : combat.defenderId,
              winnerName: attackerWins ? attackerName : defenderName,
              winnerOwnerId: attackerWins ? attackerOwnerId : defenderOwnerId,
              winnerOwnerName: attackerWins ? attackerOwnerName : defenderOwnerName,
              // 敗者資訊
              loserId: attackerWins ? combat.defenderId : combat.attackerId,
              loserName: attackerWins ? defenderName : attackerName,
              loserOwnerId: attackerWins ? defenderOwnerId : attackerOwnerId,
              loserOwnerName: attackerWins ? defenderOwnerName : attackerOwnerName,
              // 攻擊力資訊
              attackerAtk: combat.attackerAtk,
              defenderAtk: combat.defenderAtk,
              attackerSurvived: attackerWins,
              defenderSurvived: !attackerWins,
            });
          });

          // UNIT_DESTROYED: 記錄哪張卡被誰摧毀
          result.destroyedUnits.forEach(unitId => {
            const defId = unitId.split('_').slice(1, 3).join('_');
            const cardDef = cardDefinitions.get(defId);
            const cardName = cardDef?.nameZh || cardDef?.name || unitId;
            const ownerId = unitId.startsWith('p1_') ? 'player1' : 'player2';
            const ownerName = getPlayerDisplayName(ownerId, gameState);
            
            // 找到破壞這張卡的戰鬥
            const combatInfo = result.combatResults.find(c => 
              c.destroyedCardId === unitId || 
              (c.winner === 'attacker' && c.defenderId === unitId) ||
              (c.winner === 'defender' && c.attackerId === unitId)
            );
            
            const destroyerId = combatInfo ? 
              (combatInfo.winner === 'attacker' ? combatInfo.attackerId : combatInfo.defenderId) : undefined;
            const destroyerDefId = destroyerId?.split('_').slice(1, 3).join('_');
            const destroyerDef = destroyerDefId ? cardDefinitions.get(destroyerDefId) : null;
            const destroyerName = destroyerDef?.nameZh || destroyerDef?.name || destroyerId;
            const destroyerOwnerId: string | undefined = destroyerId 
              ? (destroyerId.startsWith('p1_') ? 'player1' : 'player2')
              : undefined;
            
            eventLogger.log('UNIT_DESTROYED', {
              // 被摧毀的卡牌資訊
              cardId: unitId,
              cardName,
              cardDefinitionId: defId,
              cardType: cardDef?.type ?? 'unknown',
              atk: cardDef?.atk ?? null,
              score: cardDef?.score ?? null,
              ownerId,
              ownerName,
              position: { side: ownerId, row: 'front', column: (combatInfo?.column ?? 1) as 1 | 2 | 3 | 4 | 5 },
              // 破壞來源資訊
              destroyedBy: 'combat',
              sourceCardId: destroyerId,
              sourceCardName: destroyerName,
              sourcePlayerId: destroyerOwnerId,
              sourcePlayerName: destroyerOwnerId ? getPlayerDisplayName(destroyerOwnerId, gameState) : undefined,
              // 遊戲狀態
              turnNumber: gameState.turnNumber,
              currentPhase: gameState.currentPhase,
            });
          });

          // 記錄戰鬥觸發的效果事件
          if (result.messages.length > 0) {
            result.combatResults.forEach(combat => {
              // onDestroy 效果
              if (combat.destroyedCardId) {
                const triggerId = eventLogger.log('EFFECT_TRIGGER', {
                  effectName: 'onDestroy 效果',
                  sourceCardId: combat.destroyedCardId,
                  sourceCardName: combat.destroyedCardId,
                  triggerType: 'onDestroy',
                }).id;
                eventLogger.log('EFFECT_RESOLVE', {
                  effectName: 'onDestroy 效果',
                  sourceCardId: combat.destroyedCardId,
                  result: 'success',
                  changes: result.messages.filter(m => m.includes('破壞') || m.includes('死亡')),
                }, { triggeredBy: triggerId });
              }

              // onKill / onCombatSuccess 效果
              if (combat.winner === 'attacker') {
                const killTriggerId = eventLogger.log('EFFECT_TRIGGER', {
                  effectName: '戰鬥成功效果',
                  sourceCardId: combat.attackerId,
                  sourceCardName: combat.attackerId,
                  triggerType: 'onKill',
                }).id;
                eventLogger.log('EFFECT_RESOLVE', {
                  effectName: '戰鬥成功效果',
                  sourceCardId: combat.attackerId,
                  result: 'success',
                  changes: result.messages.filter(m => m.includes('獎勵') || m.includes('戰鬥成功')),
                }, { triggeredBy: killTriggerId });
              }
            });
          }
        }

        set({ gameState: result.state });

        return {
          success: true,
          destroyedUnits: result.destroyedUnits,
          upgradedUnits: result.upgradedUnits,
          combatResults: result.combatResults,
          messages: result.messages,
        };
      },

      executeDefensePhase: () => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState) {
          return {
            success: false,
            combatOccurred: false,
            destroyedUnits: [],
            upgradedUnits: [],
            combatResults: [],
            messages: [],
          };
        }

        const result = DefensePhaseHandler.execute(gameState, cardDefinitions);

        // 記錄戰鬥結果
        if (eventLogger && result.combatOccurred) {
          result.combatResults.forEach(combat => {
            const attackerWins = combat.winner === 'attacker';
            eventLogger.log('COMBAT_RESULT', {
              column: 1,
              result: attackerWins ? 'attacker_wins' : 'defender_wins',
              attackerSurvived: attackerWins,
              defenderSurvived: !attackerWins,
            });
          });

          result.destroyedUnits.forEach(unitId => {
            eventLogger.log('UNIT_DESTROYED', {
              cardId: unitId,
              cardName: unitId,
              position: { side: 'player1' as const, row: 'front' as const, column: 1 as const },
              destroyedBy: 'combat',
            });
          });

          // 記錄戰鬥觸發的效果事件
          if (result.messages.length > 0) {
            result.combatResults.forEach(combat => {
              // onDestroy 效果
              if (combat.destroyedCardId) {
                const triggerId = eventLogger.log('EFFECT_TRIGGER', {
                  effectName: 'onDestroy 效果',
                  sourceCardId: combat.destroyedCardId,
                  sourceCardName: combat.destroyedCardId,
                  triggerType: 'onDestroy',
                }).id;
                eventLogger.log('EFFECT_RESOLVE', {
                  effectName: 'onDestroy 效果',
                  sourceCardId: combat.destroyedCardId,
                  result: 'success',
                  changes: result.messages.filter(m => m.includes('破壞') || m.includes('死亡')),
                }, { triggeredBy: triggerId });
              }

              // onKill / onCombatSuccess 效果
              if (combat.winner === 'attacker') {
                const killTriggerId = eventLogger.log('EFFECT_TRIGGER', {
                  effectName: '戰鬥成功效果',
                  sourceCardId: combat.attackerId,
                  sourceCardName: combat.attackerId,
                  triggerType: 'onKill',
                }).id;
                eventLogger.log('EFFECT_RESOLVE', {
                  effectName: '戰鬥成功效果',
                  sourceCardId: combat.attackerId,
                  result: 'success',
                  changes: result.messages.filter(m => m.includes('獎勵') || m.includes('戰鬥成功')),
                }, { triggeredBy: killTriggerId });
              }
            });
          }
        }

        set({ gameState: result.state });
        return {
          success: true,
          combatOccurred: result.combatOccurred,
          destroyedUnits: result.destroyedUnits,
          upgradedUnits: result.upgradedUnits,
          combatResults: result.combatResults,
          messages: result.messages,
        };
      },

      executeEndPhase: () => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState) return { success: false, gameEnded: false };

        const result = EndPhaseHandler.execute(gameState, cardDefinitions);

        // 記錄遊戲結束
        if (eventLogger && result.gameEnded && result.state.winnerId) {
          const newState = result.state;
          const winnerId = newState.winnerId as string;
          const winner = winnerId === 'player1' ? newState.player1 : newState.player2;
          const loser = winnerId === 'player1' ? newState.player2 : newState.player1;
          
          eventLogger.log('GAME_END', {
            winnerId,
            winnerName: winner.name,
            loserId: loser.id,
            loserName: loser.name,
            winCondition: 'score',
            finalScores: (() => {
              const s = EndPhaseHandler.getScores(newState, cardDefinitions);
              return { player1Score: s.player1 + (newState.player1.reward ?? 0), player2Score: s.player2 + (newState.player2.reward ?? 0) };
            })(),
            totalTurns: newState.turnNumber,
          });
        }

        set({ gameState: result.state });
        return { success: true, gameEnded: result.gameEnded };
      },

      getScores: () => {
        const { gameState, cardDefinitions } = get();
        if (!gameState) return { player1: 0, player2: 0 };
        return EndPhaseHandler.getScores(gameState, cardDefinitions);
      },

      // Event Logger methods
      getEventLog: () => {
        const { eventLogger, gameState } = get();
        if (!eventLogger || !gameState) return null;
        
        return eventLogger.export({
          player1: { 
            id: gameState.player1.id, 
            name: gameState.player1.name, 
            faction: gameState.player1.faction 
          },
          player2: { 
            id: gameState.player2.id, 
            name: gameState.player2.name, 
            faction: gameState.player2.faction 
          },
        });
      },

      getRecentEvents: (count) => {
        const { eventLogger } = get();
        if (!eventLogger) return [];
        return eventLogger.getRecentEvents(count);
      },

      // ============================================================
      // 選擇系統方法
      // ============================================================

      getPendingChoice: () => {
        const { pendingChoice } = get();
        return pendingChoice;
      },

      hasPendingChoice: () => {
        const { pendingChoice } = get();
        return pendingChoice !== null;
      },

      submitChoice: (result: ChoiceResult) => {
        const { gameState, cardDefinitions, pendingExecutionId, pendingChoice, gameMode } = get();
        
        if (!gameState || !pendingChoice) {
          return { success: false, error: '沒有待決選擇' };
        }

        // ── Online mode: send submit_choice command to server ──
        if (gameMode === 'online') {
          const gameId = get().onlineGameId;
          if (gameId) {
            getWSClient().sendCommand(gameId, 'submit_choice', {
              choice_id: pendingChoice.id,
              selected_card_ids: result.selectedCardIds ?? [],
              selected_option: result.selectedOptionId ?? '',
              cancelled: result.cancelled ?? false,
            });
          }
          // Server will send updated state_sync; clear local pending state
          set({
            pendingChoice: null,
            pendingExecutionId: null,
          });
          return { success: true };
        }

        // ── Local/AI mode: handle locally ──
        if (!pendingExecutionId) {
          return { success: false, error: '沒有待決執行' };
        }

        // 初始化執行器
        if (!choiceExecutor) {
          choiceExecutor = createChoiceAwareExecutor(cardDefinitions);
        }

        // 繼續執行
        const execResult = choiceExecutor.continueExecution(pendingExecutionId, result);

        if (execResult.status === 'pending_choice' && execResult.pendingChoice) {
          // 還有更多選擇需要處理
          set({
            gameState: execResult.state,
            pendingChoice: execResult.pendingChoice,
            pendingExecutionId: execResult.executionId,
          });
          return { success: true };
        }

        if (execResult.status === 'completed') {
          // 執行完成
          set({
            gameState: execResult.state,
            pendingChoice: null,
            pendingExecutionId: null,
          });
          return { success: true };
        }

        // 執行失敗
        set({
          pendingChoice: null,
          pendingExecutionId: null,
        });
        return { success: false, error: execResult.messages.join(', ') };
      },

      cancelChoice: () => {
        const { pendingChoice, gameMode } = get();

        // ── Online mode: send cancel to server ──
        if (gameMode === 'online' && pendingChoice) {
          const gameId = get().onlineGameId;
          if (gameId) {
            getWSClient().sendCommand(gameId, 'submit_choice', {
              choice_id: pendingChoice.id,
              cancelled: true,
            });
          }
          set({
            pendingChoice: null,
            pendingExecutionId: null,
          });
          return;
        }
        
        if (pendingChoice?.optional) {
          // 如果選擇是可選的，取消它
          const choiceManager = getChoiceManager();
          choiceManager.removeChoice(pendingChoice.id);
        }

        set({
          pendingChoice: null,
          pendingExecutionId: null,
        });
      },

      setPendingChoiceFromServer: (choice: PendingChoice) => {
        log.debug('設定來自伺服器的 pending choice:', choice.id, choice.type);
        set({
          pendingChoice: choice,
          pendingExecutionId: null, // No local execution ID in online mode
        });
      },

      getValidTargets: () => {
        const { gameState, cardDefinitions, pendingChoice } = get();
        
        if (!gameState || !pendingChoice || !pendingChoice.filter) {
          return [];
        }

        // 使用 ChoiceValidator 取得有效目標
        const validator = createChoiceValidator(cardDefinitions);
        return validator.getValidCards(gameState, pendingChoice.playerId, pendingChoice.filter);
      },

      // ========== 魔法/法術系統 ==========

      /**
       * 施放即時法術
       */
      castSpell: (cardId, targets) => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState) return { success: false, error: '遊戲未初始化' };

        const activePlayer = getActivePlayer(gameState);
        const card = activePlayer.hand.find((c) => c.id === cardId);

        if (!card) {
          return { success: false, error: '手牌中找不到該卡牌' };
        }

        const cardDef = cardDefinitions.get(card.definitionId);
        if (!cardDef || cardDef.type !== 'spell') {
          return { success: false, error: '該卡牌不是法術卡' };
        }

        // 場地魔法使用 placeFieldMagic
        if (cardDef.isField || cardDef.spellType === '場地') {
          return { success: false, error: '場地魔法請使用 placeFieldMagic' };
        }

        const playerName = getPlayerDisplayName(activePlayer.id, gameState);

        // 記錄 SPELL_CAST 事件
        if (eventLogger) {
          const targetInfos = targets?.map((targetId) => {
            // 嘗試在場上找目標
            for (const slot of [
              ...gameState.battlefield.player1Side.frontLine,
              ...gameState.battlefield.player1Side.backLine,
              ...gameState.battlefield.player2Side.frontLine,
              ...gameState.battlefield.player2Side.backLine,
            ]) {
              if (slot.unit?.id === targetId) {
                const targetDef = cardDefinitions.get(slot.unit.definitionId);
                return {
                  cardId: targetId,
                  cardName: targetDef?.nameZh || targetDef?.name || targetId,
                  position: slot.position,
                };
              }
            }
            return { cardId: targetId, cardName: targetId };
          });

          eventLogger.log('SPELL_CAST', {
            cardId: card.id,
            cardName: cardDef.nameZh || cardDef.name,
            cardDefinitionId: card.definitionId,
            spellType: (cardDef.spellType === '消耗' ? 'instant' : 'other') as 'instant' | 'field' | 'continuous' | 'other',
            effectText: cardDef.effectText || null,
            casterId: activePlayer.id,
            casterName: playerName,
            targets: targetInfos,
            turnNumber: gameState.turnNumber,
            currentPhase: gameState.currentPhase,
          }, { playerId: activePlayer.id });
        }

        // 從手牌移除卡牌
        const updatedPlayer = {
          ...activePlayer,
          hand: activePlayer.hand.filter((c) => c.id !== cardId),
          graveyard: [...activePlayer.graveyard, { ...card, location: 'graveyard' as const }],
        };

        let newState = updatePlayer(gameState, updatedPlayer);
        newState = addAction(newState, {
          type: 'CAST_SPELL',
          playerId: activePlayer.id,
          cardId,
          targets,
        });

        // 觸發法術效果（使用 onSpellCast 觸發類型）
        effectTriggerSystem.setDefinitions(cardDefinitions);
        
        // 建立施放後的卡牌物件
        const spellCard = {
          ...card,
          location: 'graveyard' as const,
        };

        // 使用 triggerAndExecute 配合 onSpellCast 觸發類型
        const triggerResult = effectTriggerSystem.triggerAndExecute(newState, {
          type: 'onSpellCast',
          sourceCard: spellCard,
        });

        newState = triggerResult.state;
        
        if (triggerResult.messages.length > 0) {
          log.debug('Spell effects:', triggerResult.messages.join(', '));
        }

        // 記錄 SPELL_RESOLVE 事件
        if (eventLogger) {
          eventLogger.log('SPELL_RESOLVE', {
            cardId: card.id,
            cardName: cardDef.nameZh || cardDef.name,
            cardDefinitionId: card.definitionId,
            effectText: cardDef.effectText || null,
            casterId: activePlayer.id,
            casterName: playerName,
            turnNumber: gameState.turnNumber,
            currentPhase: gameState.currentPhase,
            resolvedSuccessfully: true,
          }, { playerId: activePlayer.id });
        }

        set({ gameState: newState });
        return { success: true };
      },

      /**
       * 放置場地魔法
       */
      placeFieldMagic: (cardId) => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState) return { success: false, error: '遊戲未初始化' };

        const activePlayer = getActivePlayer(gameState);
        const card = activePlayer.hand.find((c) => c.id === cardId);

        if (!card) {
          return { success: false, error: '手牌中找不到該卡牌' };
        }

        const cardDef = cardDefinitions.get(card.definitionId);
        if (!cardDef || cardDef.type !== 'field') {
          // 也檢查 spell 類型的場地魔法
          if (cardDef?.type === 'spell' && (cardDef.isField || cardDef.spellType === '場地')) {
            // OK, 這是場地魔法
          } else {
            return { success: false, error: '該卡牌不是場地魔法' };
          }
        }

        const playerName = getPlayerDisplayName(activePlayer.id, gameState);
        const oldFieldMagic = activePlayer.fieldMagic;

        // 如果已有場地魔法，先移除舊的
        let newState = gameState;
        if (oldFieldMagic) {
          // 記錄移除事件
          if (eventLogger) {
            const oldDef = cardDefinitions.get(oldFieldMagic.definitionId);
            eventLogger.log('FIELD_MAGIC_REMOVE', {
              cardId: oldFieldMagic.id,
              cardName: oldDef?.nameZh || oldDef?.name || oldFieldMagic.id,
              cardDefinitionId: oldFieldMagic.definitionId,
              ownerId: activePlayer.id,
              ownerName: playerName,
              reason: 'replaced',
              turnNumber: gameState.turnNumber,
              currentPhase: gameState.currentPhase,
            }, { playerId: activePlayer.id });
          }
        }

        // 從手牌移除並放置場地魔法
        const fieldMagicCard = { ...card, location: 'battlefield' as const };
        const updatedPlayer = {
          ...activePlayer,
          hand: activePlayer.hand.filter((c) => c.id !== cardId),
          graveyard: oldFieldMagic 
            ? [...activePlayer.graveyard, { ...oldFieldMagic, location: 'graveyard' as const }]
            : activePlayer.graveyard,
          fieldMagic: fieldMagicCard,
        };

        newState = updatePlayer(newState, updatedPlayer);

        // 記錄 FIELD_MAGIC_PLACE 事件
        if (eventLogger) {
          eventLogger.log('FIELD_MAGIC_PLACE', {
            cardId: card.id,
            cardName: cardDef.nameZh || cardDef.name,
            cardDefinitionId: card.definitionId,
            effectText: cardDef.effectText || null,
            ownerId: activePlayer.id,
            ownerName: playerName,
            replacedCardId: oldFieldMagic?.id || null,
            replacedCardName: oldFieldMagic 
              ? (cardDefinitions.get(oldFieldMagic.definitionId)?.nameZh || oldFieldMagic.id)
              : null,
            turnNumber: gameState.turnNumber,
            currentPhase: gameState.currentPhase,
          }, { playerId: activePlayer.id });
        }

        // 觸發場地魔法效果（使用 onFieldEnter 觸發類型）
        effectTriggerSystem.setDefinitions(cardDefinitions);

        // 使用 triggerAndExecute 處理場地魔法的放置效果
        const triggerResult = effectTriggerSystem.triggerAndExecute(newState, {
          type: 'onFieldEnter',
          sourceCard: fieldMagicCard,
        });

        newState = triggerResult.state;
        
        if (triggerResult.messages.length > 0) {
          log.debug('Field magic effects:', triggerResult.messages.join(', '));
        }

        set({ gameState: newState });
        return { success: true };
      },

      /**
       * 移除場地魔法
       */
      removeFieldMagic: (playerId) => {
        const { gameState, cardDefinitions, eventLogger } = get();
        if (!gameState) return { success: false, error: '遊戲未初始化' };

        const player = playerId === gameState.player1.id ? gameState.player1 : gameState.player2;
        const fieldMagic = player.fieldMagic;

        if (!fieldMagic) {
          return { success: false, error: '沒有場地魔法可移除' };
        }

        const playerName = getPlayerDisplayName(playerId, gameState);
        const cardDef = cardDefinitions.get(fieldMagic.definitionId);

        // 記錄 FIELD_MAGIC_REMOVE 事件
        if (eventLogger) {
          eventLogger.log('FIELD_MAGIC_REMOVE', {
            cardId: fieldMagic.id,
            cardName: cardDef?.nameZh || cardDef?.name || fieldMagic.id,
            cardDefinitionId: fieldMagic.definitionId,
            ownerId: playerId,
            ownerName: playerName,
            reason: 'destroyed',
            turnNumber: gameState.turnNumber,
            currentPhase: gameState.currentPhase,
          }, { playerId });
        }

        // 移除場地魔法到墓地
        const updatedPlayer = {
          ...player,
          graveyard: [...player.graveyard, { ...fieldMagic, location: 'graveyard' as const }],
          fieldMagic: null,
        };

        const newState = updatePlayer(gameState, updatedPlayer);
        set({ gameState: newState });
        return { success: true };
      },

      /**
       * 取得玩家的場地魔法
       */
      getFieldMagic: (playerId) => {
        const { gameState } = get();
        if (!gameState) return null;

        const player = playerId === gameState.player1.id ? gameState.player1 : gameState.player2;
        return player.fieldMagic || null;
      },

      // ============================================================
      // 連線對戰模式
      // ============================================================

      gameMode: 'local' as const,
      onlineGameId: null,
      onlinePlayerSide: null,

      /**
       * 開始連線對戰
       * 收到伺服器 game_start 事件時呼叫
       */
      startOnlineGame: (gameId, playerSide, initialState) => {
        log.debug('開始連線對戰:', gameId, playerSide);

        const definitions = loadAllCards();
        const mappedState = initialState
          ? mapServerViewToGameState(initialState, playerSide)
          : null;

        set({
          gameMode: 'online',
          onlineGameId: gameId,
          onlinePlayerSide: playerSide,
          isInitialized: true,
          gameState: mappedState,
          cardDefinitions: definitions,
        });

        // Start game timer if state has timer info
        if (mappedState?.gameTimeLimit) {
          const timer = getGlobalTimer();
          let elapsed = 0;
          if (mappedState.gameStartedAt) {
            elapsed = Math.floor((Date.now() - new Date(mappedState.gameStartedAt).getTime()) / 1000);
          }
          timer.startGameTimer(mappedState.gameTimeLimit, elapsed);
        }

        log.debug('連線對戰已初始化, gameState:', mappedState);
      },

      /**
       * 處理伺服器推送的遊戲事件
       * 不更新狀態（由 state_sync 驅動），僅用於 UI 效果
       */
      handleOnlineEvent: (event) => {
        const { gameMode } = get();
        if (gameMode !== 'online') return;

        log.debug('連線事件:', event.event, event.data);

        // 根據伺服器事件觸發音效
        switch (event.event) {
          case 'unit_summoned':
          case 'spell_cast':
            AudioManager.play('playCard');
            break;
          case 'attacks_declared':
            AudioManager.play('attack');
            break;
          case 'combat_result':
            AudioManager.play('damage');
            break;
          case 'phase_changed':
            AudioManager.play('phaseChange');
            break;
        }
      },

      /**
       * 處理遊戲結束
       * 不立即重置 online 狀態，等 VictoryScreen 完成後再清理
       */
      handleOnlineGameEnd: (winnerId, winCondition, isWinner) => {
        log.debug('連線對戰結束:', { winnerId, winCondition, isWinner });

        const { gameState } = get();
        if (gameState) {
          // 將伺服器 UUID 映射為 local player side
          let localWinnerId: string | null = null;
          if (winnerId) {
            if (winnerId === gameState.player1.id) {
              localWinnerId = 'player1';
            } else if (winnerId === gameState.player2.id) {
              localWinnerId = 'player2';
            }
          }
          set({
            gameState: { ...gameState, gameEnded: true, winnerId: localWinnerId },
          });
        }
      },

      /**
       * 更新完整遊戲狀態（收到 state_sync）
       * Also extracts and sets pending choice if present in the server state.
       */
      updateOnlineGameState: (state) => {
        const { onlinePlayerSide } = get();
        if (!onlinePlayerSide) return;

        const mappedState = mapServerViewToGameState(state, onlinePlayerSide);

        // Extract pending choice from server state (for reconnection scenarios)
        const serverChoice = extractPendingChoice(state);
        if (serverChoice) {
          set({
            gameState: mappedState,
            pendingChoice: serverChoice as PendingChoice,
            pendingExecutionId: null,
          });
        } else {
          set({
            gameState: mappedState,
            // Clear pending choice if server no longer has one
            // (but only if we're in online mode with no local execution)
            ...(get().gameMode === 'online' ? { pendingChoice: null, pendingExecutionId: null } : {}),
          });
        }
      },
    })),
    { name: 'GameStore' }
  )
);

// Expose store globally for debugging and to ensure single instance across HMR
if (typeof window !== 'undefined') {
  (window as unknown as { __GAME_STORE__: typeof useGameStore }).__GAME_STORE__ = useGameStore;
}
